import { AutoCompleteOption } from "../components/form/APAutoComplete";
import {
  ExistingAssetAndLiability,
  FinanceApplicationInputs,
  NewAssetAndLiability,
  RequiredFields,
} from "../dossier/financeApplication";
import {
  AddressResidenceStatus,
  ASSET_AND_LIABILITY_ID_ASSET_AND_DEBT,
  ASSET_AND_LIABILITY_ID_DEBT,
  ASSET_AND_LIABILITY_ID_LOAN,
  ASSET_AND_LIABILITY_ID_PROPERTY,
  ASSET_AND_LIABILITY_ID_VEHICLE,
  AssetAndLiabilityType,
  AustralianResidencyStatus,
} from "../types/Dossier";
import { isAmountGreaterThanPercentage } from "./number";

export const showIdentityDriversLicenseFields = (
  hasDriversLicense: boolean
) => {
  return !!hasDriversLicense;
};

export const showEmploymentPayslipFields = (hasPayslip: boolean) => {
  return !!hasPayslip;
};

export const showIdentityPassportUploadField = (
  residencyStatus: AustralianResidencyStatus | null,
  hasDriversLicense: boolean
) => {
  return (
    (residencyStatus && residencyStatus !== "citizen") || !hasDriversLicense
  );
};

export const showIdentityVisaUploadField = (
  residencyStatus: AustralianResidencyStatus | null
) => {
  return (
    residencyStatus &&
    residencyStatus !== "citizen" &&
    residencyStatus !== "nz-citizen"
  );
};

export const showIdentityVisaDescriptionFields = (
  residencyStatus: AustralianResidencyStatus | null
) => {
  return (
    residencyStatus &&
    residencyStatus !== "citizen" &&
    residencyStatus !== "nz-citizen" &&
    residencyStatus !== "permanent-resident"
  );
};

export const showResidenceLandlordFields = (
  residenceStatus: AddressResidenceStatus
) => {
  return residenceStatus === "rent";
};

export const showResidencePreviousAddress = (currentYearsAt: string) => {
  return !!currentYearsAt && parseInt(currentYearsAt) < 2;
};

export const showEmploymentTaxReturns = (
  employmentType: AutoCompleteOption | null
) => {
  return !!employmentType && employmentType.id === "director";
};

export const showEmploymentPreviousEmployment = (currentYearsAt: string) => {
  return !!currentYearsAt && parseInt(currentYearsAt) < 2;
};

export const showSpouseLatestPayslip = (
  annualIncome: string,
  spouseAnnualIncome: string
) => {
  return isAmountGreaterThanPercentage(
    parseFloat(spouseAnnualIncome),
    parseFloat(annualIncome),
    1.25
  );
};

export const isRentIncomeRequired = (
  residenceStatus: AddressResidenceStatus
) => {
  return residenceStatus === "rent" || residenceStatus === "boarding";
};

export const hasAssetOrLiability = (
  assetAndLiabilityTypes: AssetAndLiabilityType[],
  itemId: string
) => {
  const selectedType =
    assetAndLiabilityTypes.find((type) => `${type.typeId}` === itemId) || null;
  const hasAsset = !!selectedType && selectedType.isAsset;
  const hasLiability = !!selectedType && selectedType.isLiability;

  return [hasAsset, hasLiability];
};

export const hasOwingAmount = (owing: string) => {
  return !!owing && parseFloat(owing) > 0;
};

export const showAssetIncome = (type: AutoCompleteOption | null) => {
  return !!type && type.id === `${ASSET_AND_LIABILITY_ID_PROPERTY}`;
};

export const showAssetRepayment = (type: AutoCompleteOption | null) => {
  const typesWithRepayments = [
    `${ASSET_AND_LIABILITY_ID_PROPERTY}`,
    `${ASSET_AND_LIABILITY_ID_VEHICLE}`,
    `${ASSET_AND_LIABILITY_ID_LOAN}`,
    `${ASSET_AND_LIABILITY_ID_DEBT}`,
    `${ASSET_AND_LIABILITY_ID_ASSET_AND_DEBT}`,
  ];

  return !!type && typesWithRepayments.includes(type.id);
};

export const showAssetReplacingVehicle = (type: AutoCompleteOption | null) => {
  return !!type && type.id === `${ASSET_AND_LIABILITY_ID_VEHICLE}`;
};

export const isCreditCardProvidersRequired = (totalCreditCardLimit: string) => {
  return !!totalCreditCardLimit && parseFloat(totalCreditCardLimit) > 0;
};

export type MonthlyIncomeTotals = {
  totalIncome: number;
  totalExpenses: number;
  netIncome: number;
};

export const calculateIncomeTotals = (
  netMonthlyPay: string,
  spouseNetMonthlyPay: string,
  propertyIncome: string,
  investmentIncome: string,
  govtBenefitIncome: string,
  otherIncome: string,
  rentMonthly: string,
  mortgageMonthly: string,
  repaymentsMonthly: string,
  vehicleMonthly: string,
  educationMonthly: string,
  generalExpenseMonthly: string,
  otherExpensesMonthly: string
): MonthlyIncomeTotals => {
  const monthlyPay = netMonthlyPay ? parseFloat(netMonthlyPay) : 0;
  const monthlySpousePay = spouseNetMonthlyPay
    ? parseFloat(spouseNetMonthlyPay)
    : 0;

  const totalIncome = Math.round(
    [propertyIncome, investmentIncome, govtBenefitIncome, otherIncome].reduce(
      (sum: number, next: string) => {
        if (next) {
          return sum + parseFloat(next) / 12;
        }
        return sum;
      },
      monthlyPay + monthlySpousePay
    )
  );

  const totalExpenses = Math.round(
    [
      rentMonthly,
      mortgageMonthly,
      repaymentsMonthly,
      vehicleMonthly,
      educationMonthly,
      generalExpenseMonthly,
      otherExpensesMonthly,
    ].reduce((sum: number, next: string) => {
      if (next) {
        return sum + parseFloat(next);
      }
      return sum;
    }, 0)
  );

  const netIncome = totalIncome - totalExpenses;

  return {
    totalIncome,
    totalExpenses,
    netIncome,
  };
};

const calculateTotalAmount = (
  existingAssetsAndLiabilities: ExistingAssetAndLiability[],
  newAssetsAndLiabilities: NewAssetAndLiability[],
  amountType: "asset" | "liability",
  listType: "existing" | "new"
) => {
  const fieldName = amountType === "asset" ? "assetValue" : "liabilityValue";
  const list =
    listType === "existing"
      ? existingAssetsAndLiabilities
      : newAssetsAndLiabilities;
  const totalAmount = list
    ? list.reduce((acc, item) => {
        return (
          acc +
          (!item.toBeRemoved && item[fieldName]
            ? parseFloat(item[fieldName])
            : 0)
        );
      }, 0)
    : 0;
  return totalAmount;
};

export type AssetAndLiabilityTotals = {
  totalAssets: number;
  totalLiabilities: number;
  totalNetWorth: number;
};

export const calculateAssetAndLiabilityTotals = (
  existingAssetsAndLiabilities: ExistingAssetAndLiability[],
  newAssetsAndLiabilities: NewAssetAndLiability[],
  totalCreditCardLimit: string
): AssetAndLiabilityTotals => {
  const totalAssets =
    calculateTotalAmount(
      existingAssetsAndLiabilities,
      newAssetsAndLiabilities,
      "asset",
      "existing"
    ) +
    calculateTotalAmount(
      existingAssetsAndLiabilities,
      newAssetsAndLiabilities,
      "asset",
      "new"
    );
  const totalLiabilities =
    calculateTotalAmount(
      existingAssetsAndLiabilities,
      newAssetsAndLiabilities,
      "liability",
      "existing"
    ) +
    calculateTotalAmount(
      existingAssetsAndLiabilities,
      newAssetsAndLiabilities,
      "liability",
      "new"
    ) +
    (totalCreditCardLimit ? parseFloat(totalCreditCardLimit) : 0);
  const totalNetWorth = totalAssets - totalLiabilities;

  return {
    totalAssets,
    totalLiabilities,
    totalNetWorth,
  };
};

export const getRequiredFields = (
  data: FinanceApplicationInputs,
  assetAndLiabilityTypes: AssetAndLiabilityType[]
): RequiredFields => {
  const requiredFields: RequiredFields = {
    personal: [
      "firstName",
      "lastName",
      "dateOfBirth",
      "mobile",
      "numberOfDependents",
      "maritalStatus",
    ],
    identity: ["residencyStatus"],
    residence: [
      "currentStreetAddress",
      "currentSuburb",
      "currentState",
      "currentPostcode",
      "currentResidenceStatus",
      "currentYearsAt",
    ],
    employment: [
      "currentEmployerName",
      "currentEmploymentType",
      "currentJobTitle",
      "currentEmploymentYearsAt",
      "annualSalaryTotal",
      "prevPayslipImage",
      "annualSalaryPerPay",
      "annualSalaryPayFrequency",
    ],
    income: [
      "generalExpensesAmount",
      "generalExpensesFrequency",
      "acceptTerms",
    ],
    assetsAndLiabilities: ["totalCreditCardLimit", "totalCreditCardBalance"],
    existingAssetsAndLiabilities: [],
    newAssetsAndLiabilities: [],
  };

  // Conditional Identity Fields
  if (showIdentityDriversLicenseFields(data.hasDriversLicense)) {
    requiredFields.identity.push(
      "driversLicenseNumber",
      "driversLicenseCardNumber",
      "driversState",
      "driversLicenseType",
      "driversLicenseFrontImage",
      "driversLicenseBackImage"
    );
  }

  if (
    showIdentityPassportUploadField(
      data.residencyStatus,
      data.hasDriversLicense
    )
  ) {
    requiredFields.identity.push("passportImage");
  }
  if (showIdentityVisaUploadField(data.residencyStatus)) {
    requiredFields.identity.push("visaImage");
  }
  if (showIdentityVisaDescriptionFields(data.residencyStatus)) {
    requiredFields.identity.push("visaType", "visaExpiry");
  }

  // Conditional Residency fields
  if (showResidenceLandlordFields(data.currentResidenceStatus)) {
    requiredFields.residence.push("currentLandlord", "currentLandlordPhone");
  }
  if (showResidencePreviousAddress(data.currentYearsAt)) {
    requiredFields.residence.push(
      "previousStreetAddress",
      "previousSuburb",
      "previousState",
      "previousYearsAt"
    );
  }

  // Conditional Employment fields
  if (showEmploymentTaxReturns(data.currentEmploymentType)) {
    requiredFields.employment.push("lastTaxReturnImage", "prevTaxReturnImage");
  }
  if (showEmploymentPreviousEmployment(data.currentEmploymentYearsAt)) {
    requiredFields.employment.push(
      "previousEmployerName",
      "previousJobTitle",
      "previousEmploymentYearsAt"
    );
  }

  if (showEmploymentPayslipFields(data.hasPayslip)) {
    requiredFields.employment.push("latestPayslipImage");
  }

  // Conditional Income fields
  if (data.spouseIncludeIncome) {
    requiredFields.income.push(
      "spouseAnnualIncome",
      "spouseSalaryPerPay",
      "spousePayFrequency",
      "spouseEmploymentType"
    );

    if (showEmploymentTaxReturns(data.spouseEmploymentType)) {
      requiredFields.income.push(
        "spouseLastTaxReturnImage",
        "spousePrevTaxReturnImage"
      );
    }

    if (
      showSpouseLatestPayslip(data.annualSalaryTotal, data.spouseAnnualIncome)
    ) {
      requiredFields.income.push("spouseLatestPayslipImage");
    }
  }
  if (isRentIncomeRequired(data.currentResidenceStatus)) {
    requiredFields.income.push("rentAmount", "rentFrequency");
  }

  // Conditional Assets and Liability fields
  if (data.assetsAndLiabilities) {
    data.assetsAndLiabilities.forEach((item, index) => {
      if (!item.toBeRemoved) {
        const [hasAsset, hasLiability] = item.type
          ? hasAssetOrLiability(assetAndLiabilityTypes, item.type.id)
          : [false, false];
        requiredFields.existingAssetsAndLiabilities[index] = [];
        requiredFields.existingAssetsAndLiabilities[index].push("description");
        requiredFields.existingAssetsAndLiabilities[index].push("type");

        if (hasAsset) {
          requiredFields.existingAssetsAndLiabilities[index].push("assetValue");
        }

        if (hasLiability) {
          requiredFields.existingAssetsAndLiabilities[index].push(
            "liabilityValue"
          );
          if (hasOwingAmount(item.liabilityValue)) {
            requiredFields.existingAssetsAndLiabilities[index].push(
              "financier"
            );
          }
        }

        if (showAssetIncome(item.type)) {
          requiredFields.existingAssetsAndLiabilities[index].push(
            "incomeMonthly"
          );
        }

        if (
          showAssetRepayment(item.type) &&
          hasOwingAmount(item.liabilityValue)
        ) {
          requiredFields.existingAssetsAndLiabilities[index].push(
            "repaymentMonthly"
          );
        }

        if (showAssetReplacingVehicle(item.type)) {
          requiredFields.existingAssetsAndLiabilities[index].push(
            "disposePriorToSettlement"
          );
        }
      }
    });
  }
  if (data.newAssetsAndLiabilities) {
    data.newAssetsAndLiabilities.forEach((item, index) => {
      if (!item.toBeRemoved) {
        const [hasAsset, hasLiability] = item.type
          ? hasAssetOrLiability(assetAndLiabilityTypes, item.type.id)
          : [false, false];
        requiredFields.newAssetsAndLiabilities[index] = [];
        requiredFields.newAssetsAndLiabilities[index].push("description");
        requiredFields.newAssetsAndLiabilities[index].push("type");

        if (hasAsset) {
          requiredFields.newAssetsAndLiabilities[index].push("assetValue");
        }

        if (hasLiability) {
          requiredFields.newAssetsAndLiabilities[index].push("liabilityValue");

          if (hasOwingAmount(item.liabilityValue)) {
            requiredFields.newAssetsAndLiabilities[index].push("financier");
          }
        }

        if (showAssetIncome(item.type)) {
          requiredFields.newAssetsAndLiabilities[index].push("incomeMonthly");
        }

        if (
          showAssetRepayment(item.type) &&
          hasOwingAmount(item.liabilityValue)
        ) {
          requiredFields.newAssetsAndLiabilities[index].push(
            "repaymentMonthly"
          );
        }

        if (showAssetReplacingVehicle(item.type)) {
          requiredFields.newAssetsAndLiabilities[index].push(
            "disposePriorToSettlement"
          );
        }
      }
    });
  }
  if (isCreditCardProvidersRequired(data.totalCreditCardLimit)) {
    requiredFields.assetsAndLiabilities.push("creditCardProviders");
  }

  return requiredFields;
};
